import { Phone } from "lucide-react";
import PageHero from "../components/PageHero";
import Breadcrumbs from "../components/Breadcrumbs";
import BookCallCTA from "../components/BookCallCTA";
import { NAP } from "../lib/config";

export default function AccessibilityPage() {
  return (
    <>
      <PageHero
        eyebrow="Accessibility"
        title="One Accessible-layout Queen room — please call to confirm what you need."
        intro="We keep one room configured for mobility access. Every guest's needs are different, so please call the front desk to confirm the specific features that make your stay work."
        image="/images/room-accessible-1.jpg"
        metaTitle="Accessibility — Westbridge Inn & Suites Clinton MO"
        metaDesc="Accessibility information for Westbridge Inn & Suites in Clinton, Missouri. Accessible-layout Queen room available — call the front desk to confirm specific features."
      />
      <Breadcrumbs items={[{ label: "Accessibility" }]} />

      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-16 md:py-20 max-w-3xl">
          <h2 className="wb-h3">What we can confirm</h2>
          <ul className="mt-6 space-y-3 text-[#4A4641] wb-body text-base">
            <li>An accessible-layout Queen room with a bath configured for mobility access.</li>
            <li>Ground-floor rooms available on request.</li>
            <li>Free accessible parking spaces near the entrance.</li>
            <li>Elevator access is not part of the property — we're a single-story-focused layout for the accessible room.</li>
            <li>Service animals are welcome — please tell the desk when you book.</li>
          </ul>

          <h2 className="wb-h3 mt-12">What to ask when you call</h2>
          <ul className="mt-6 space-y-3 text-[#4A4641] wb-body text-base">
            <li>Bath configuration (roll-in shower vs. tub-shower details) — we'll describe exactly what the room has.</li>
            <li>Bed height and clearance.</li>
            <li>Doorway widths and clearances between furniture.</li>
            <li>Distance from parking to room entrance.</li>
            <li>Any equipment needs — grab bars, shower chairs, visual alarms — we'll tell you honestly what's in the room and what we can arrange.</li>
          </ul>

          <div className="mt-12 wb-card p-8 bg-[#F5F0E6] border-[#E5DFD3]">
            <h3 className="wb-h3">Call the front desk</h3>
            <p className="wb-body mt-3">
              Please call <a href={`tel:${NAP.phoneTel}`} className="text-[#B94A15] font-bold hover:underline" data-testid="accessibility-phone-link">{NAP.phone}</a> before booking so we can confirm the specific features you need. We'd rather spend five minutes on the phone getting it right than have you arrive to something that doesn't work for your stay.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`tel:${NAP.phoneTel}`} className="wb-btn-primary" data-testid="accessibility-call">
                <Phone className="h-4 w-4" /> {NAP.phone}
              </a>
            </div>
          </div>

          <h2 className="wb-h3 mt-12">Website accessibility</h2>
          <p className="wb-body mt-4">
            This site is being built with accessibility in mind — semantic landmarks, keyboard-navigable menus, alt text on images, visible focus states, and sufficient color contrast. If you encounter an accessibility barrier, please email <a href={`mailto:${NAP.email}`} className="text-[#B94A15] font-bold hover:underline">{NAP.email}</a> or call the front desk. We'd like to know so we can fix it.
          </p>
        </div>
      </section>

      <BookCallCTA />
    </>
  );
}
